import React, { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createCourseModule,
  updateCourseModule,
  deleteCourseModule,
  reorderCourseModules,
  CourseModule,
} from "../firebase/courseModuleService";
import {
  createCourseChapter,
  updateCourseChapter,
  deleteCourseChapter,
  reorderCourseChapters,
  CourseChapter,
} from "../firebase/courseChapterService";
import {
  createCourse,
  updateCourse,
  deleteCourse,
  reorderCourses,
  Course,
} from "../firebase/courseService";
import {
  createArticle,
  updateArticle,
  deleteArticle,
  reorderArticles,
  Article,
} from "../firebase/articleService";
import { Timestamp } from "firebase/firestore";
import { globalRateLimiter } from "../utils/rateLimiter";
import {
  courseKeys,
  coursesQuery,
  courseChaptersQuery,
  courseModulesQuery,
  articlesQuery,
} from "../queries/contentQueries";

interface CourseContextType {
  courses: Course[];
  chapters: CourseChapter[];
  courseModules: CourseModule[];
  articles: Article[];
  loading: boolean;
  error: Error | null;
  refreshAll: () => Promise<void>;

  addCourse: (course: Omit<Course, "id" | "createdAt" | "updatedAt">) => Promise<string>;
  editCourse: (id: string, course: Partial<Omit<Course, "id" | "createdAt" | "updatedAt">>) => Promise<void>;
  removeCourse: (id: string) => Promise<void>;
  reorderCoursesList: (orderedIds: string[]) => Promise<void>;

  addChapter: (chapter: Omit<CourseChapter, "id" | "createdAt" | "updatedAt">) => Promise<string>;
  editChapter: (
    id: string,
    chapter: Partial<Omit<CourseChapter, "id" | "createdAt" | "updatedAt">>
  ) => Promise<void>;
  removeChapter: (id: string) => Promise<void>;
  reorderChapters: (orderedIds: string[]) => Promise<void>;

  addCourseModule: (courseModule: Omit<CourseModule, "id" | "createdAt" | "updatedAt">) => Promise<string>;
  editCourseModule: (
    id: string,
    courseModule: Partial<Omit<CourseModule, "id" | "createdAt" | "updatedAt">>
  ) => Promise<void>;
  removeCourseModule: (id: string) => Promise<void>;
  reorderModules: (orderedIds: string[]) => Promise<void>;

  addArticle: (article: Omit<Article, "id" | "createdAt" | "updatedAt">) => Promise<string>;
  editArticle: (
    id: string,
    article: Partial<Omit<Article, "id" | "createdAt" | "updatedAt">>
  ) => Promise<void>;
  removeArticle: (id: string) => Promise<void>;
  reorderArticlesList: (orderedIds: string[]) => Promise<void>;
}

const EMPTY_COURSES: Course[] = [];
const EMPTY_CHAPTERS: CourseChapter[] = [];
const EMPTY_MODULES: CourseModule[] = [];
const EMPTY_ARTICLES: Article[] = [];

const byOrder = (a: { order: number }, b: { order: number }) => a.order - b.order;

// Data lives in the TanStack Query cache; the provider is kept so the app tree doesn't change.
export const CourseProvider: React.FC<{ children: ReactNode }> = ({ children }) => <>{children}</>;

export const useCourseModules = (): CourseContextType => {
  const queryClient = useQueryClient();
  const coursesResult = useQuery(coursesQuery);
  const chaptersResult = useQuery(courseChaptersQuery);
  const modulesResult = useQuery(courseModulesQuery);
  const articlesResult = useQuery(articlesQuery);
  const results = [coursesResult, chaptersResult, modulesResult, articlesResult];

  // Each list is cached under its own key; writes patch the cached list in place.
  const patch = <T,>(queryKey: readonly unknown[], update: (prev: T[]) => T[]) =>
    queryClient.setQueryData<T[]>(queryKey, (prev) => update(prev ?? []));

  const refreshAll = async () => {
    await queryClient.refetchQueries({ queryKey: courseKeys.all });
  };

  // Courses
  const addCourse = async (course: Omit<Course, "id" | "createdAt" | "updatedAt">): Promise<string> => {
    if (!globalRateLimiter.isAllowed("add-course")) {
      throw new Error("Too many requests. Please wait a moment before trying again.");
    }
    const id = await createCourse(course);
    patch<Course>(coursesQuery.queryKey, (prev) =>
      [...prev, { ...course, id, createdAt: Timestamp.now(), updatedAt: Timestamp.now() }].sort(byOrder)
    );
    return id;
  };

  const editCourse = async (
    id: string,
    course: Partial<Omit<Course, "id" | "createdAt" | "updatedAt">>
  ): Promise<void> => {
    await updateCourse(id, course);
    patch<Course>(coursesQuery.queryKey, (prev) =>
      prev
        .map((c) => (c.id === id ? ({ ...c, ...course, updatedAt: Timestamp.now() } as Course) : c))
        .sort(byOrder)
    );
  };

  const removeCourse = async (id: string): Promise<void> => {
    await deleteCourse(id);
    patch<Course>(coursesQuery.queryKey, (prev) => prev.filter((c) => c.id !== id));
  };

  const reorderCoursesList = async (orderedIds: string[]): Promise<void> => {
    await reorderCourses(orderedIds);
    await refreshAll();
  };

  // Chapters
  const addChapter = async (
    chapter: Omit<CourseChapter, "id" | "createdAt" | "updatedAt">
  ): Promise<string> => {
    if (!globalRateLimiter.isAllowed("add-chapter")) {
      throw new Error("Too many requests. Please wait a moment before trying again.");
    }
    const id = await createCourseChapter(chapter);
    patch<CourseChapter>(courseChaptersQuery.queryKey, (prev) =>
      [...prev, { ...chapter, id, createdAt: Timestamp.now(), updatedAt: Timestamp.now() }].sort(byOrder)
    );
    return id;
  };

  const editChapter = async (
    id: string,
    chapter: Partial<Omit<CourseChapter, "id" | "createdAt" | "updatedAt">>
  ): Promise<void> => {
    await updateCourseChapter(id, chapter);
    patch<CourseChapter>(courseChaptersQuery.queryKey, (prev) =>
      prev
        .map((c) => (c.id === id ? ({ ...c, ...chapter, updatedAt: Timestamp.now() } as CourseChapter) : c))
        .sort(byOrder)
    );
  };

  const removeChapter = async (id: string): Promise<void> => {
    await deleteCourseChapter(id);
    patch<CourseChapter>(courseChaptersQuery.queryKey, (prev) => prev.filter((c) => c.id !== id));
  };

  const reorderChapters = async (orderedIds: string[]): Promise<void> => {
    await reorderCourseChapters(orderedIds);
    await refreshAll();
  };

  // Modules
  const addCourseModule = async (
    courseModule: Omit<CourseModule, "id" | "createdAt" | "updatedAt">
  ): Promise<string> => {
    if (!globalRateLimiter.isAllowed("add-course-module")) {
      throw new Error("Too many requests. Please wait a moment before trying again.");
    }
    const id = await createCourseModule(courseModule);
    patch<CourseModule>(courseModulesQuery.queryKey, (prev) =>
      [...prev, { ...courseModule, id, createdAt: Timestamp.now(), updatedAt: Timestamp.now() }].sort(byOrder)
    );
    return id;
  };

  const editCourseModule = async (
    id: string,
    courseModule: Partial<Omit<CourseModule, "id" | "createdAt" | "updatedAt">>
  ): Promise<void> => {
    if (!globalRateLimiter.isAllowed(`edit-course-module-${id}`)) {
      throw new Error("Too many update requests. Please wait a moment before trying again.");
    }
    await updateCourseModule(id, courseModule);
    patch<CourseModule>(courseModulesQuery.queryKey, (prev) =>
      prev
        .map((m) => (m.id === id ? ({ ...m, ...courseModule, updatedAt: Timestamp.now() } as CourseModule) : m))
        .sort(byOrder)
    );
  };

  const removeCourseModule = async (id: string): Promise<void> => {
    await deleteCourseModule(id);
    patch<CourseModule>(courseModulesQuery.queryKey, (prev) => prev.filter((m) => m.id !== id));
  };

  const reorderModules = async (orderedIds: string[]): Promise<void> => {
    await reorderCourseModules(orderedIds);
    await refreshAll();
  };

  // Articles
  const addArticle = async (
    article: Omit<Article, "id" | "createdAt" | "updatedAt">
  ): Promise<string> => {
    if (!globalRateLimiter.isAllowed("add-article")) {
      throw new Error("Too many requests. Please wait a moment before trying again.");
    }
    const id = await createArticle(article);
    patch<Article>(articlesQuery.queryKey, (prev) =>
      [...prev, { ...article, id, createdAt: Timestamp.now(), updatedAt: Timestamp.now() }].sort(byOrder)
    );
    return id;
  };

  const editArticle = async (
    id: string,
    article: Partial<Omit<Article, "id" | "createdAt" | "updatedAt">>
  ): Promise<void> => {
    if (!globalRateLimiter.isAllowed(`edit-article-${id}`)) {
      throw new Error("Too many update requests. Please wait a moment before trying again.");
    }
    await updateArticle(id, article);
    patch<Article>(articlesQuery.queryKey, (prev) =>
      prev
        .map((a) => (a.id === id ? ({ ...a, ...article, updatedAt: Timestamp.now() } as Article) : a))
        .sort(byOrder)
    );
  };

  const removeArticle = async (id: string): Promise<void> => {
    await deleteArticle(id);
    patch<Article>(articlesQuery.queryKey, (prev) => prev.filter((a) => a.id !== id));
  };

  const reorderArticlesList = async (orderedIds: string[]): Promise<void> => {
    await reorderArticles(orderedIds);
    await refreshAll();
  };

  return {
    courses: coursesResult.data ?? EMPTY_COURSES,
    chapters: chaptersResult.data ?? EMPTY_CHAPTERS,
    courseModules: modulesResult.data ?? EMPTY_MODULES,
    articles: articlesResult.data ?? EMPTY_ARTICLES,
    loading: results.some((r) => r.isPending),
    error: (results.find((r) => r.error)?.error ?? null) as Error | null,
    refreshAll,
    addCourse,
    editCourse,
    removeCourse,
    reorderCoursesList,
    addChapter,
    editChapter,
    removeChapter,
    reorderChapters,
    addCourseModule,
    editCourseModule,
    removeCourseModule,
    reorderModules,
    addArticle,
    editArticle,
    removeArticle,
    reorderArticlesList,
  };
};
